import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Statistic, Row, Col, Select, Switch, Tag, Tooltip, Alert, Space, message, Button } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, MinusOutlined, UserOutlined, WhatsAppOutlined, FileExcelOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { getBranches } from '../../api/branches';
import { exportReenrollmentReport } from '../../api/reports';
import BranchSelect from '../common/BranchSelect';
import useAuthStore from '../../store/authStore';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import { raporIndir } from '../../utils/downloadBlob';
// Ham `905551112233` okunmuyor (frontend/CLAUDE.md → telefon kuralı).
import { formatPhone } from '../../utils/phone';

/**
 * "Önceki dönemlerde vardı, son döneme yazılmadı" raporu.
 *
 * Dönem adları KODA GÖMÜLÜ DEĞİL: sütunlar şubenin son N döneminden
 * türetiliyor, en sağdaki (en son başlayan) dönem hedef. Böylece her yeni
 * dönemde rapor kendiliğinden kayıyor, kimsenin kodu güncellemesi
 * gerekmiyor.
 */

const DURUM_GORUNUM = {
  kayitli: {
    ikon: <CheckCircleFilled style={{ color: '#52c41a' }} />,
    aciklama: 'Kayıtlı — aktif dersi var',
  },
  iptal: {
    ikon: <CloseCircleFilled style={{ color: '#fa8c16' }} />,
    aciklama: 'Kaydı açılmış ama tüm dersleri iptal edilmiş',
  },
  yok: {
    ikon: <MinusOutlined style={{ color: '#bfbfbf' }} />,
    aciklama: 'Bu dönemde hiç kaydı yok',
  },
};

const DURUM_ETIKET = {
  active:    { renk: 'green',  metin: 'Aktif' },
  passive:   { renk: 'red',    metin: 'Pasif' },
  candidate: { renk: 'orange', metin: 'Aday' },
};

const ReenrollmentReportTab = () => {
  const { user } = useAuthStore();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id ?? null);
  const [termCount, setTermCount] = useState(3);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hata, setHata] = useState(null);

  const [pageSize, setPageSize] = usePersistedPageSize('report-reenrollment', 25);
  const [page, setPage] = useState(1);
  const [sorter, setSorter] = useState({ field: null, order: null });
  const [indiriliyor, setIndiriliyor] = useState(false);

  useEffect(() => {
    getBranches()
      .then(res => {
        const liste = res.data?.data || res.data || [];
        setBranches(liste);
        setBranchId(onceki => onceki ?? liste[0]?.id ?? null);
      })
      .catch(() => {});
  }, []);

  // Süzgeçlerin HEPSİ bağımlılık dizisinde: biri unutulursa liste bir önceki
  // seçime ait kalır (bkz. CLAUDE.md — stale closure tuzağı).
  const getir = useCallback(async (hedefSayfa = 1, boyut = pageSize, sort = sorter) => {
    if (!branchId) return;
    setLoading(true);
    setHata(null);
    try {
      const { data } = await api.get('/reports/reenrollment', {
        params: {
          branch_id: branchId,
          term_count: termCount,
          only_missing: onlyMissing ? 1 : 0,
          include_inactive_students: includeInactive ? 1 : 0,
          page: hedefSayfa,
          per_page: boyut,
          sort_by: sort?.field || undefined,
          sort_direction: sort?.order === 'ascend' ? 'asc' : (sort?.order === 'descend' ? 'desc' : undefined),
        },
      });
      setVeri(data);
      setPage(hedefSayfa);
    } catch (e) {
      setHata(e?.response?.status === 403
        ? 'Bu raporu görüntüleme yetkiniz yok.'
        : 'Rapor yüklenemedi.');
      setVeri(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, termCount, onlyMissing, includeInactive, pageSize, sorter]);

  // Süzgeç değişince ilk sayfaya dön — aksi halde kullanıcı bambaşka bir
  // listenin 3. sayfasına bakıyor olur.
  useEffect(() => { getir(1); }, [branchId, termCount, onlyMissing, includeInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  // SÜZGEÇLERİN TAMAMI dosyaya da gidiyor. Biri atlanırsa ekranda
  // "sadece kaydolmayanlar" görünürken Excel'e herkes iner ve kimse
  // farkı sorgulamaz — arama listesi yanlış kişilere dağıtılır.
  const excelIndir = async () => {
    setIndiriliyor(true);
    try {
      await raporIndir(exportReenrollmentReport({
        branch_id: branchId,
        term_count: termCount,
        only_missing: onlyMissing ? 1 : 0,
        include_inactive_students: includeInactive ? 1 : 0,
        sort_by: sorter?.field || undefined,
        sort_direction: sorter?.order === 'ascend' ? 'asc' : (sorter?.order === 'descend' ? 'desc' : undefined),
      }), 'donem-gecisi.xlsx');
    } catch (e) {
      message.error(e?.message || 'Excel indirilemedi.');
    } finally {
      setIndiriliyor(false);
    }
  };

  const donemler = veri?.terms || [];
  const hedefId  = veri?.target_term;

  const sortProps = (field) => ({
    sorter: true,
    sortOrder: sorter.field === field ? sorter.order : null,
  });

  const columns = [
    {
      title: 'Öğrenci',
      dataIndex: 'student_name',
      fixed: 'left',
      ...sortProps('student_name'),
      render: (ad, satir) => (
        <Link to={`/students/${satir.student_id}`} className="font-medium">{ad}</Link>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'student_status',
      width: 90,
      ...sortProps('student_status'),
      render: (d) => {
        const e = DURUM_ETIKET[d] || { renk: 'default', metin: d };
        return <Tag color={e.renk}>{e.metin}</Tag>;
      },
    },
    {
      // Arama listesi olduğu için veli adı gerekiyor — özellikle kardeşlerde
      // hangi aileyle konuşulacağını ayırt etmek için.
      title: 'Veli',
      dataIndex: 'parent_name',
      ...sortProps('parent_name'),
      render: v => v || <span className="text-gray-300">—</span>,
    },
    {
      /*
        TELEFON — bu rapor bir ARAMA LİSTESİ ama numarayı hiç göstermiyordu
        (18.10.2026). Yönetici listeyi çıkarıp her satır için Kullanıcılar
        ekranına gidiyordu.

        "Veli" sütunu TÜM velileri yazar, burada TEK numara var: aranacak
        veliyi sunucu seçiyor (bildirim almayı açmış ve numarası dolu olan
        — `ReportController::aranacakVeli`).
      */
      title: 'Veli Tel',
      dataIndex: 'parent_phone',
      width: 170,
      ...sortProps('parent_phone'),
      render: (v, r) => {
        if (!v) return <span className="text-gray-300">—</span>;
        return (
          <span className="inline-flex items-center gap-2">
            {formatPhone(v)}
            {/* Metin sunucuda hazırlanıp WhatsApp'a taşınıyor, GÖNDER'e
                kişi basıyor — uygulama kimseye mesaj GÖNDERMİYOR.

                Şablon Borç Raporu'ndan AYRI (`reenrollment_reminder_whatsapp`):
                "bakiyeniz var" ile "yeni döneme yazılın" bambaşka iki çağrı.

                `r.whatsapp` alanını sunucu yalnızca `users.whatsapp` izni
                olana koyuyor; alan yoksa ikon da yok. Burada ayrıca izin
                kontrolü YAPILMIYOR — arayüzde gizlemek yetki değildir. */}
            {r.whatsapp && (
              <Tooltip title="Kayıt yenileme mesajı gönder">
                <a
                  href={`https://wa.me/${r.whatsapp.phone}?text=${encodeURIComponent(r.whatsapp.text)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#25D366', lineHeight: 1 }}
                  aria-label={`${r.parent_name || 'Veli'} için kayıt yenileme mesajı gönder`}
                >
                  <WhatsAppOutlined style={{ fontSize: 15 }} />
                </a>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    // Dönem sütunları veriden geliyor — hiçbir dönem adı kodda yazmıyor.
    ...donemler.map(t => ({
      title: (
        <Tooltip title={`${t.start_date} — ${t.end_date}${t.is_active ? '' : ' (pasif dönem)'}`}>
          <span className={t.id === hedefId ? 'font-bold' : undefined}>{t.name}</span>
        </Tooltip>
      ),
      key: `term-${t.id}`,
      align: 'center',
      width: 130,
      render: (_, satir) => {
        const durum = satir.terms?.[t.id] ?? 'yok';
        const g = DURUM_GORUNUM[durum] || DURUM_GORUNUM.yok;
        return <Tooltip title={g.aciklama}>{g.ikon}</Tooltip>;
      },
    })),
    {
      title: 'Son Derslik',
      dataIndex: 'last_classroom',
      ...sortProps('last_classroom'),
      render: v => v || '—',
    },
    {
      title: 'Gün',
      dataIndex: 'last_day',
      width: 110,
      ...sortProps('last_day'),
      render: v => v || '—',
    },
    {
      title: 'Saat',
      dataIndex: 'last_time',
      width: 80,
      ...sortProps('last_time'),
      render: v => v || '—',
    },
  ];

  const ozet = veri?.summary;
  const hedefAd = donemler.find(t => t.id === hedefId)?.name;

  return (
    <div>
      <Space wrap className="mb-4">
        <BranchSelect branches={branches} value={branchId} onChange={setBranchId} style={{ width: 200 }} />
        <Select
          value={termCount}
          onChange={setTermCount}
          style={{ width: 150 }}
          options={[2, 3, 4, 5, 6].map(n => ({ value: n, label: `Son ${n} dönem` }))}
        />
        <span>
          <Switch size="small" checked={onlyMissing} onChange={setOnlyMissing} />{' '}
          Sadece kaydolmayanlar
        </span>
        <span>
          <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />{' '}
          Pasif/aday öğrenciler dahil
        </span>
      </Space>

      {hata && <Alert type="error" showIcon message={hata} className="mb-4" />}
      {veri?.message && <Alert type="info" showIcon message={veri.message} className="mb-4" />}

      {ozet && (
        <Row gutter={16} className="mb-4">
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Önceki dönemlerde devam eden"
                value={ozet.onceki_toplam}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title={hedefAd ? `${hedefAd} dönemine yazılan` : 'Yazılan'}
                value={ozet.yenileyen}
                suffix={`(%${ozet.oran})`}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Yazılmayan"
                value={ozet.yenilemeyen}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">
          <CheckCircleFilled style={{ color: '#52c41a' }} /> kayıtlı ·{' '}
          <CloseCircleFilled style={{ color: '#fa8c16' }} /> kaydolup iptal etmiş ·{' '}
          <MinusOutlined style={{ color: '#bfbfbf' }} /> kaydı yok
        </div>
        {/* Liste boşken (ör. kıyas için iki dönem yoksa) düğme kapalı —
            basılsaydı uç .xlsx değil JSON döner ve kullanıcının eline
            Excel'in açamadığı bir dosya geçerdi. */}
        <Button
          icon={<FileExcelOutlined />}
          onClick={excelIndir}
          loading={indiriliyor}
          disabled={!veri?.total}
        >
          Excel İndir
        </Button>
      </div>

      <Table
        dataSource={veri?.data || []}
        rowKey="student_id"
        columns={columns}
        size="small"
        loading={loading}
        scroll={{ x: 'max-content' }}
        // Sayfalama ve sıralama tek elden: pagination.onChange ayrıca tanımlı
        // kalsaydı sayfa değişiminde iki istek giderdi.
        onChange={(pag, _filters, next) => {
          const yeni = next?.order ? { field: next.field, order: next.order } : { field: null, order: null };
          const sortDegisti = yeni.field !== sorter.field || yeni.order !== sorter.order;
          setSorter(yeni);
          setPageSize(pag.pageSize);
          getir(sortDegisti ? 1 : pag.current, pag.pageSize, yeni);
        }}
        pagination={{
          current: veri?.current_page || page,
          pageSize: veri?.per_page || pageSize,
          total: veri?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100'],
          showTotal: (t) => `Toplam ${t} öğrenci`,
        }}
      />
    </div>
  );
};

export default ReenrollmentReportTab;
